import { ITEM_TYPES } from 'constants/itemType';
import { NotificationPriority, NotificationType } from 'hooks/useNotification';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import type { UserNotificationWithDetails } from 'hooks/useUserNotifications';
import { HELENE_ADVISORIES } from 'components/MapPage/CorporateViewTabs/LayersTab/cfa-demo/historicalHurricanesHeleneData';
import type {
  MemphisAssetAtRisk,
  MemphisRegionalThreats,
} from './memphisThreatNotification';

/** Stable id for the hardcoded EPA Gulf tropical-storm inbox item. */
export const EPA_GULF_THREAT_NOTIFICATION_ID = 'demo-epa-gulf-tropical-storm';

const EPA_GULF_THREAT_SENT_AT = '2024-09-24T15:30:00.000Z';

const TS_ADVISORY =
  HELENE_ADVISORIES.find((a) => a.category === 'TS') ?? HELENE_ADVISORIES[1];

export const EPA_GULF_THREAT_PROVENANCE =
  '43 Assets to be Impacted by Tropical Storm';

export const EPA_GULF_THRESHOLD_DESCRIPTION =
  'Tropical storm category threshold exceeded — Helene forecast cone contains EPA Gulf resources';

export const EPA_GULF_LINKED_EVENT_NAME =
  'Tropical Storm — EPA assets in forecast cone';

export const EPA_GULF_RESOURCES_COMMITTED =
  '43 resources are within the tropical storm cone';

export const EPA_GULF_ORG_EVENT_DEMO_KEY = 'epa-gulf-cone';

export const EPA_GULF_RESOURCE_NOTES_PREFIX = 'epa-gulf-npl:';
const EPA_GULF_LEGACY_RESOURCE_NOTES_PREFIX = 'epa-gulf-facility:';

export const EPA_GULF_SYMBOLOGY = 'facility';

export type EpaGulfFacility = {
  id: string;
  name: string;
  city: string;
  state: string;
  lon: number;
  lat: number;
};

export const EPA_GULF_FACILITIES: EpaGulfFacility[] = [
  {
    id: 'epa-gulfport-division',
    name: 'EPA Gulf of Mexico Division',
    city: 'Gulfport',
    state: 'MS',
    lon: -89.093,
    lat: 30.367,
  },
  {
    id: 'epa-bayou-bonfouca',
    name: 'Bayou Bonfouca Superfund',
    city: 'Slidell',
    state: 'LA',
    lon: -89.791,
    lat: 30.273,
  },
  {
    id: 'epa-ag-street',
    name: 'Agriculture Street Landfill Superfund',
    city: 'New Orleans',
    state: 'LA',
    lon: -90.037,
    lat: 29.986,
  },
  {
    id: 'epa-petro-processors',
    name: 'Petro-Processors Superfund',
    city: 'Baton Rouge',
    state: 'LA',
    lon: -91.187,
    lat: 30.526,
  },
  {
    id: 'epa-malone-texas-city',
    name: 'Malone Service Co. Superfund',
    city: 'Texas City',
    state: 'TX',
    lon: -94.903,
    lat: 29.384,
  },
  {
    id: 'epa-houston-lab',
    name: 'EPA Houston Laboratory',
    city: 'Houston',
    state: 'TX',
    lon: -95.369,
    lat: 29.76,
  },
];

export function formatEpaFacilityLabel(facility: EpaGulfFacility): string {
  return `${facility.name} (${facility.city}, ${facility.state})`;
}

export const EPA_RESPONSE_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'epa-checklist-create-waiver',
    kind: 'org_action',
    label: 'Create waiver',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'epa-checklist-notify-nsc',
    kind: 'send_notification',
    label: 'Notify White House NSC of likely impact to EPA Gulf resources',
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate: 'White House NSC: EPA Gulf resources in {eventName} cone',
      bodyTemplate:
        'White House NSC: {eventName} is likely to impact EPA resources in FEMA Region IV. Review assets at risk in PRATUS and confirm receipt.',
      emailSubject:
        'White House NSC: likely impact to EPA Gulf resources — {eventName}',
      emailMessage:
        'White House NSC:\n\n{eventName} places EPA resources in FEMA Region IV inside the forecast cone. Review the Response Checklist and assets at risk in PRATUS.',
      appMessage:
        'White House NSC: {eventName} — likely impact to EPA Gulf resources. Review PRATUS for details.',
    },
  },
  {
    id: 'epa-checklist-preposition-osc',
    kind: 'org_action',
    label:
      'Pre-position On-Scene Coordinators at Superfund and drinking-water sites',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'epa-checklist-water-guidance',
    kind: 'org_action',
    label:
      'Issue protective-action guidance for public water systems in the cone',
    orgAction: { status: 'Planned', poc: null },
  },
];

export const EPA_RESPONSE_CHECKLIST_LABELS = EPA_RESPONSE_CHECKLIST_ITEMS.map(
  (item) => item.label
);

export const EPA_RESPONSE_CHECKLIST_ORG_ACTION_LABELS =
  EPA_RESPONSE_CHECKLIST_ITEMS.filter((item) => item.kind === 'org_action').map(
    (item) => item.label
  );

export const EPA_ORG_ACTION_DESCRIPTION =
  'EPA Gulf demo — auto-created from tropical-storm Response Checklist. Planned / Unassigned.';

export function isEpaGulfOrgActionName(
  name: string | null | undefined
): boolean {
  return !!name && EPA_RESPONSE_CHECKLIST_ORG_ACTION_LABELS.includes(name);
}

export function epaGulfResourceNotesMarker(facilityId: string): string {
  return `${EPA_GULF_RESOURCE_NOTES_PREFIX}${facilityId}`;
}

function notesPrefix(notes: string): string | undefined {
  if (notes.startsWith(EPA_GULF_RESOURCE_NOTES_PREFIX)) {
    return EPA_GULF_RESOURCE_NOTES_PREFIX;
  }
  if (notes.startsWith(EPA_GULF_LEGACY_RESOURCE_NOTES_PREFIX)) {
    return EPA_GULF_LEGACY_RESOURCE_NOTES_PREFIX;
  }
  return undefined;
}

export function epaFacilityIdFromResourceNotes(
  notes: string | null | undefined
): string | undefined {
  if (!notes) return undefined;
  const prefix = notesPrefix(notes);
  if (!prefix) return undefined;
  const id = notes.slice(prefix.length);
  return id || undefined;
}

export function isEpaGulfResourceSet(resource: {
  notes?: string | null;
}): boolean {
  return !!resource.notes?.startsWith(EPA_GULF_RESOURCE_NOTES_PREFIX);
}

function buildAssetFromFacility(facility: EpaGulfFacility): MemphisAssetAtRisk {
  return {
    id: facility.id,
    resource: formatEpaFacilityLabel(facility),
    risk:
      `${formatEpaFacilityLabel(facility)} sits inside the Helene ` +
      `forecast cone while the system is at Tropical Storm intensity ` +
      `(${TS_ADVISORY.maxWindMph} mph). Secure the site, confirm containment, and report status to the RRT.`,
    location: [facility.lon, facility.lat],
    operationalStatus: 'Partially Operational',
  };
}

export function getEpaRegionalThreats(): MemphisRegionalThreats {
  return {
    region: 'EPA Gulf — Helene forecast cone',
    description:
      'Tropical Storm Helene places the following EPA resources inside the National Hurricane Center forecast cone at elevated risk:',
    threats: EPA_GULF_FACILITIES.map(buildAssetFromFacility),
    responseChecklist: EPA_RESPONSE_CHECKLIST_ITEMS.map((item) => ({
      id: item.id,
      label: item.label,
    })),
  };
}

export function isHardcodedEpaGulfThreatNotification(
  notificationId: string | null | undefined
): boolean {
  return notificationId === EPA_GULF_THREAT_NOTIFICATION_ID;
}

export function getEpaGulfThreatNotification(
  groupId: string
): UserNotificationWithDetails {
  return {
    userGuid: 'system',
    notificationId: EPA_GULF_THREAT_NOTIFICATION_ID,
    groupId,
    archived: false,
    acknowledged: false,
    canArchive: false,
    createdAt: EPA_GULF_THREAT_SENT_AT,
    acknowledgedAt: null,
    archivedAt: null,
    priority: NotificationPriority.WARNING,
    status: 'sent',
    acknowledgementRequired: false,
    sourceItemId: '',
    sourceItemType: ITEM_TYPES.Sitrep,
    sentAt: EPA_GULF_THREAT_SENT_AT,
    scheduledDate: null,
    incidentId: null,
    aors: [],
    appMessage:
      'Likely impact to EPA resources due to Tropical Storm in FEMA Region IV.',
    emailSubject: null,
    emailMessage: null,
    smsMessage: null,
    teamsMessage: null,
    sendEmail: null,
    sendSms: null,
    sendTeams: null,
    actionType: null,
    createdByName: EPA_GULF_THREAT_PROVENANCE,
  };
}
